"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import {
    Phone,
    MapPin,
    Send,
    Instagram,
    Facebook,
    Linkedin,
    ChevronDown,
    Menu,
    X
} from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { QuickEnquiryModal } from "@/components/shared/QuickEnquiryModal";
interface NavItem {
    title: string;
    slug: string;
}

interface HeaderProps {
    servicesList?: NavItem[];
    solutionsList?: NavItem[];
}

export function Header({ servicesList = [], solutionsList = [] }: HeaderProps) {
    const NAV_LINKS = [
        { label: "Home", href: "/" },
        { label: "About Us", href: "/about" },
        {
            label: "Services",
            href: "/services",
            dropdown: true,
            items: servicesList.map(s => ({
                label: s.title,
                href: `/services/${s.slug}`
            }))
        },
        {
            label: "Solutions",
            href: "/solutions",
            dropdown: true,
            items: solutionsList.map(s => ({
                label: s.title,
                href: `/solutions/${s.slug}`
            }))
        },
        { label: "Products", href: "/products" },
        { label: "Case Studies", href: "/case-studies" },
        { label: "Blog", href: "/blog" },
        { label: "Contact Us", href: "/contact" },
    ];
    const [isScrolled, setIsScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isEnquiryOpen, setIsEnquiryOpen] = useState(false);

    const menuRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLElement>(null);

    // Handle scroll for sticky nav
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 140); // Height of top bar roughly
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Mobile Menu Animation
    useGSAP(() => {
        if (mobileMenuOpen) {
            gsap.to(overlayRef.current, { autoAlpha: 1, duration: 0.3 });
            gsap.to(menuRef.current, { x: "0%", duration: 0.5, ease: "power3.out" });
        } else {
            gsap.to(overlayRef.current, { autoAlpha: 0, duration: 0.3 });
            gsap.to(menuRef.current, { x: "100%", duration: 0.5, ease: "power3.in" });
        }
    }, { scope: containerRef, dependencies: [mobileMenuOpen] });

    return (
        <header ref={containerRef} className="w-full z-50">
            {/* Top Section (Contact & Info) - Hidden on mobile, visible on lg */}
            <div className="hidden border-b border-gray-100 bg-white py-3 xl:py-4 lg:block">
                <div className="container mx-auto flex items-center justify-between gap-4 px-4">
                    <Link href="/" className="shrink-0" aria-label="Colombo Net Solutions home">
                        <Image
                            src="/cns-logo.png"
                            alt="Colombo Net Solutions"
                            width={220}
                            height={80}
                            className="h-12 w-auto object-contain xl:h-16 xl:py-2"
                            priority
                        />
                    </Link>

                    <div className="ml-auto flex min-w-0 items-center gap-4 xl:gap-8">
                        <div className="flex items-center gap-2 border-r border-gray-200 pr-4 xl:gap-3 xl:pr-6">
                            <div className="rounded-full bg-blue-50 p-2 text-blue-600">
                                <Send className="h-4 w-4 -rotate-45 xl:h-5 xl:w-5" />
                            </div>
                            <div className="min-w-0">
                                <label className="block text-[10px] font-semibold uppercase text-gray-500 xl:text-xs">WhatsApp</label>
                                <a href="tel:+94707336060" className="text-xs font-bold text-gray-800 transition-colors hover:text-primary xl:text-sm">+94 70 733 6060</a>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 border-r border-gray-200 pr-4 xl:gap-3 xl:pr-6">
                            <div className="rounded-full bg-blue-50 p-2 text-blue-600">
                                <Phone className="h-4 w-4 xl:h-5 xl:w-5" />
                            </div>
                            <div className="min-w-0">
                                <label className="block text-[10px] font-semibold uppercase text-gray-500 xl:text-xs">0114 502 905</label>
                                <a href="mailto:info@cns.lk" className="text-xs font-bold text-gray-800 transition-colors hover:text-primary xl:text-sm">info@cns.lk</a>
                            </div>
                        </div>

                        <div className="hidden items-center gap-3 pr-6 2xl:flex">
                            <div className="rounded-full bg-blue-50 p-2 text-blue-600">
                                <MapPin className="h-5 w-5" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold uppercase text-gray-500">258/10B, Kotte Road</label>
                                <span className="text-sm font-bold text-gray-800">Nugegoda, 10250</span>
                            </div>
                        </div>

                        <div className="flex shrink-0 items-center gap-3 xl:flex-col xl:items-end xl:gap-2">
                            <div className="hidden gap-3 text-gray-600 xl:flex">
                                <a href="https://www.instagram.com/colombo_net/" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-primary" aria-label="Instagram"><Instagram className="h-4 w-4" /></a>
                                <a href="https://www.facebook.com/profile.php?id=100063541633192" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-primary" aria-label="Facebook"><Facebook className="h-4 w-4" /></a>
                                <a href="https://www.linkedin.com/company/colombonetsolutions/" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-primary" aria-label="LinkedIn"><Linkedin className="h-4 w-4" /></a>
                            </div>
                            <button
                                onClick={() => setIsEnquiryOpen(true)}
                                className="rounded-full bg-blue-600 px-4 py-1.5 text-[11px] font-bold text-white shadow-lg shadow-blue-200 transition-colors hover:bg-blue-700 xl:px-5 xl:py-2 xl:text-xs"
                            >
                                Quick Enquiry
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation Bar */}
            <nav
                className={`transition-all duration-300 ${isScrolled
                    ? "fixed top-0 left-0 right-0 shadow-lg z-50 py-5 lg:py-2 bg-white/90 lg:bg-[#001746]/90 backdrop-blur-md"
                    : "relative py-3 lg:py-2 bg-white lg:bg-[#001746]"
                    }`}
            >
                <div className="container mx-auto px-4 flex justify-between items-center">
                    {/* Mobile Logo (Visible only on Mobile) */}
                    <div className={`lg:hidden flex items-center`}>
                        <Link href="/" aria-label="Colombo Net Solutions home">
                            <Image
                                src="/cns-logo.png"
                                alt="Colombo Net Solutions"
                                width={140}
                                height={50}
                                className="object-contain h-10 w-auto"
                                priority
                            />
                        </Link>
                    </div>

                    {/* Desktop Menu */}
                    <ul className="mx-auto hidden h-[30px] items-center gap-5 lg:flex xl:gap-8">
                        {NAV_LINKS.map((link) => (
                            <li key={link.label} className="group relative">
                                <Link
                                    href={link.href}
                                    className="flex items-center gap-1 py-4 text-[13px] font-medium text-white transition-colors hover:text-blue-400 xl:text-[15px]"
                                >
                                    {link.label}
                                    {link.dropdown && <ChevronDown className="w-3 h-3 opacity-70 group-hover:rotate-180 transition-transform" />}
                                </Link>
                                {/* Dropdown Menu */}
                                {link.dropdown && link.items && (
                                    <div className="absolute top-full left-0 w-60 bg-white text-gray-800 shadow-xl rounded-b-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-2 group-hover:translate-y-0 z-50 border-t-2 border-primary">
                                        <div className="py-2">
                                            {link.items.map((item) => (
                                                <Link
                                                    key={item.href}
                                                    href={item.href}
                                                    className="block px-6 py-3 hover:bg-gray-50 text-sm font-medium border-b border-gray-100 last:border-0 hover:text-primary transition-colors"
                                                >
                                                    {item.label}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>

                    {/* Mobile Menu Toggle */}
                    <button
                        className="lg:hidden text-gray-800"
                        onClick={() => setMobileMenuOpen(true)}
                        aria-label="Open mobile menu"
                    >
                        <Menu className="w-7 h-7" />
                    </button>
                </div>
            </nav>

            {/* Mobile Menu Drawer (Always rendered, animated via GSAP) */}
            <div
                ref={menuRef}
                className="fixed inset-y-0 right-0 w-[80%] max-w-sm bg-white shadow-2xl z-[60] p-6 lg:hidden translate-x-full" // Initial state hidden
            >
                <div className="flex justify-between items-center mb-8">
                    <Image src="/cns-logo.png" alt="CNS" width={120} height={40} className="object-contain" />
                    <button onClick={() => setMobileMenuOpen(false)} className="text-gray-500 hover:text-red-500" aria-label="Close mobile menu">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="flex flex-col gap-4 overflow-y-auto max-h-[80vh]">
                    {NAV_LINKS.map(link => (
                        <div key={link.label}>
                            <Link
                                href={link.href}
                                className="text-lg font-medium text-gray-800 border-b border-gray-100 pb-2 flex justify-between items-center"
                                onClick={() => !link.dropdown && setMobileMenuOpen(false)}
                            >
                                {link.label}
                            </Link>
                            {/* Mobile Dropdown Items */}
                            {link.dropdown && link.items && (
                                <div className="pl-4 mt-2 flex flex-col gap-2">
                                    {link.items.map(item => (
                                        <Link
                                            key={item.href}
                                            href={item.href}
                                            className="text-sm text-gray-600 hover:text-primary py-1 block"
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            {item.label}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                    <button
                        onClick={() => {
                            setMobileMenuOpen(false);
                            setIsEnquiryOpen(true);
                        }}
                        className="mt-4 bg-primary text-white text-center py-3 rounded-md font-bold w-full"
                    >
                        Quick Enquiry
                    </button>
                </div>
            </div>

            {/* Mobile Menu Overlay */}
            <div
                ref={overlayRef}
                className="fixed inset-0 bg-black/50 z-[55] lg:hidden invisible opacity-0" // Initial state hidden
                onClick={() => setMobileMenuOpen(false)}
            />

            {/* Quick Enquiry Modal */}
            <QuickEnquiryModal
                isOpen={isEnquiryOpen}
                onClose={() => setIsEnquiryOpen(false)}
            />
        </header>
    );
}

